import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { CalendarCheck, PlayCircle, Mail } from "lucide-react";
import heroLandscape from "@/assets/hero-landscape.jpg";

const HERO_IMAGE = heroLandscape;

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.65, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] } }),
};

export const Hero = () => {
  return (
    <section data-testid="hero-section" className="relative flex min-h-[92vh] overflow-hidden bg-legacy pt-[112px] text-ivory md:min-h-screen md:pt-[128px]">
      {/* Production image slot: replace this temporary in-repo visual with public/homepage-hero-background.jpg, 2400×1600px minimum, approx. 3:2, focal interest on the right half. */}
      <img
        src={HERO_IMAGE}
        alt=""
        aria-hidden="true"
        data-testid="hero-background-slot"
        className="absolute inset-0 h-full w-full object-cover object-[62%_center] opacity-75 saturate-[0.75] sepia-[0.12] md:object-[66%_center]"
      />
      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(17,17,17,0.92)_0%,rgba(17,17,17,0.78)_42%,rgba(17,17,17,0.42)_72%,rgba(17,17,17,0.58)_100%)]" />

      <div className="container-lr relative z-10 flex flex-1 items-center py-20 md:py-28">
        <div className="max-w-3xl">
          <motion.div variants={fade} initial="hidden" animate="show" custom={0} className="flex items-center gap-4">
            <span className="gold-rule" />
            <p className="eyebrow text-gold" data-testid="hero-eyebrow">Financial Clarity • Control • Opportunity • Legacy</p>
          </motion.div>
          <motion.h1 variants={fade} initial="hidden" animate="show" custom={1} data-testid="hero-headline" className="mt-6 font-serif text-5xl font-semibold leading-[0.98] tracking-display text-ivory text-balance sm:text-6xl lg:text-7xl">
            Financial clarity <span className="accent-word">changes everything.</span>
          </motion.h1>
          <motion.p variants={fade} initial="hidden" animate="show" custom={2} className="mt-7 max-w-2xl font-serif text-2xl leading-tight text-ivory/95 md:text-3xl">
            Build a financial system that creates more freedom today and more opportunities tomorrow.
          </motion.p>
          <motion.p variants={fade} initial="hidden" animate="show" custom={3} className="mt-5 max-w-2xl text-base leading-relaxed text-taupe md:text-lg">
            Legacy Life &amp; Retirement exists to shorten the distance between where people are today and the life they&rsquo;re capable of creating by making financial education understandable, actionable, and centered around systems instead of products.
          </motion.p>
          <motion.div variants={fade} initial="hidden" animate="show" custom={4} className="mt-9 flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center">
            <Link to="/strategy-call" data-testid="hero-primary-cta" className="btn-base btn-gold"><CalendarCheck size={18} />Book Your Free Strategy Call</Link>
            <Link to="/resources#save-spend-overview" data-testid="hero-secondary-cta" className="btn-base btn-outline-light"><PlayCircle size={18} />Watch the Overview</Link>
            <a href="#newsletter" data-testid="hero-newsletter-cta" className="btn-base btn-outline-light"><Mail size={18} />Join Our Weekly Newsletter</a>
          </motion.div>
          <motion.div variants={fade} initial="hidden" animate="show" custom={5} data-testid="hero-supporting-line" className="mt-10 flex items-center gap-4">
            <span className="hidden h-px w-12 shrink-0 bg-gold/70 sm:block" />
            <p className="font-serif text-2xl italic leading-snug text-gold md:text-[1.9rem]">
              The information is free, but the knowledge is priceless.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
